import { UploadedFile, FileUploadResponse } from '../../schemas/chat';
import api from '../../lib/api';
import type { AxiosResponse } from '../../lib/api';
import type {
  UploadProgress,
  KnowledgeBaseFileUploadResponse,
  FileProgressResponse,
} from '../../types/fileUpload';

// File upload API with generic type support
export const fileAPI = {
  /**
   * Upload a file to the server
   * @param file - The file to upload
   * @param onProgress - Optional callback for upload progress (0-100)
   * @returns Promise with file upload response containing file info and upload ID
   */
  uploadFile: (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<AxiosResponse<FileUploadResponse>> => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post<FileUploadResponse>('/api/v1/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent: any) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    } as any);
  },

  /**
   * Upload file to knowledge base
   * Uses the new API endpoint: /api/v1/knowledge-bases/{kb_id}/files/upload
   * This API completes upload, binding to KB, and starts processing in one step
   *
   * @param knowledgeBaseId - 知识库ID
   * @param file - 要上传的文件
   * @param onProgress - 上传进度回调函数（可选）
   * @returns Promise<AxiosResponse<KnowledgeBaseFileUploadResponse>>
   *
   * @example
   * ```typescript
   * const response = await fileAPI.uploadFileToKnowledgeBase(
   *   'kb-id',
   *   file,
   *   (progress) => {
   *     console.log(`Uploaded: ${progress.percentage}%`);
   *   }
   * );
   * console.log(response.data.file.id); // 文件ID
   * console.log(response.data.upload_id); // 上传ID
   * ```
   */
  uploadFileToKnowledgeBase: (
    knowledgeBaseId: string,
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<AxiosResponse<KnowledgeBaseFileUploadResponse>> => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post<KnowledgeBaseFileUploadResponse>(
      `/api/v1/knowledge-bases/${knowledgeBaseId}/files/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        // onUploadProgress 是 Axios 的原生配置项，用于监听文件上传进度
        // Axios 会在上传过程中定期触发此回调，提供已上传字节数和总字节数
        // 注意：由于我们的 ApiClient 包装了 axios，需要确保配置能正确传递到底层 axios 实例
        onUploadProgress: (progressEvent: { loaded: number; total?: number }) => {
          if (onProgress && progressEvent.total) {
            const progress: UploadProgress = {
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total),
            };
            onProgress(progress);
          }
        },
      } as any // 使用 as any 是因为 TypeScript 类型定义可能不完整，但运行时 Axios 支持此配置
    );
  },

  getFiles: (): Promise<AxiosResponse<UploadedFile[]>> => api.get<UploadedFile[]>('/api/v1/files/'),

  deleteFile: (fileId: string): Promise<AxiosResponse<void>> =>
    api.delete<void>(`/api/v1/files/${fileId}`),

  // Additional file management endpoints
  getFileInfo: (fileId: string): Promise<AxiosResponse<UploadedFile>> =>
    api.get<UploadedFile>(`/api/v1/files/${fileId}`),

  updateFileMetadata: (
    fileId: string,
    metadata: Record<string, unknown>
  ): Promise<AxiosResponse<UploadedFile>> =>
    api.put<UploadedFile>(`/api/v1/files/${fileId}`, { metadata }),

  /**
   * Upload a cover image for knowledge base
   * Uses the new API endpoint: /api/v1/knowledge-bases/covers/upload
   *
   * @param file - The cover image file to upload
   * @param onProgress - Optional callback for upload progress (0-100)
   * @returns Promise<AxiosResponse<{ success: boolean; message: string; url: string }>> - Returns the API path of the uploaded cover
   *
   * @example
   * ```typescript
   * const response = await fileAPI.uploadCover(file, (progress) => {
   *   console.log(`Uploaded: ${progress}%`);
   * });
   * console.log(response.data.url); // e.g., "/api/v1/knowledge-bases/covers/1_320495f022644aed93ac7711b8d22732.jpeg"
   * ```
   */
  uploadCover: (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<AxiosResponse<{ success: boolean; message: string; url: string }>> => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post<{ success: boolean; message: string; url: string }>(
      '/api/v1/knowledge-bases/covers/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: any) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      } as any
    );
  },

  /**
   * Get the full URL for a knowledge base cover image
   * Accepts either a full API path or just the filename
   *
   * @param pathOrFilename - The API path (e.g., "/api/v1/knowledge-bases/covers/1_xxx.jpeg") or filename
   * @returns The full URL to access the cover image
   *
   * @example
   * ```typescript
   * // With full API path
   * const coverUrl = fileAPI.getCoverUrl('/api/v1/knowledge-bases/covers/1_320495f022644aed93ac7711b8d22732.jpeg');
   * // Returns: "http://api.example.com/api/v1/knowledge-bases/covers/1_320495f022644aed93ac7711b8d22732.jpeg"
   *
   * // With just filename
   * const coverUrl2 = fileAPI.getCoverUrl('1_320495f022644aed93ac7711b8d22732.jpeg');
   * // Returns: "http://api.example.com/api/v1/knowledge-bases/covers/1_320495f022644aed93ac7711b8d22732.jpeg"
   * ```
   */
  getCoverUrl: (pathOrFilename: string): string => {
    const baseURL = process.env.REACT_APP_API_URL || '';

    // If it's already a full API path, just prepend the base URL
    if (pathOrFilename.startsWith('/api/v1/knowledge-bases/covers/')) {
      return `${baseURL}${pathOrFilename}`;
    }

    // Otherwise, treat it as a filename and construct the full path
    return `${baseURL}/api/v1/knowledge-bases/covers/${pathOrFilename}`;
  },

  /**
   * Get file processing progress
   * Uses the API endpoint: /api/v1/files/{file_id}/progress
   *
   * @param fileId - The file ID to check progress for
   * @returns Promise<AxiosResponse<FileProgressResponse>>
   *
   * @example
   * ```typescript
   * const response = await fileAPI.getFileProgress('file-id');
   * console.log(response.data.status); // 'processing' | 'completed'
   * console.log(response.data.message); // '正在提取文本...'
   * ```
   */
  getFileProgress: (fileId: string): Promise<AxiosResponse<FileProgressResponse>> =>
    api.get<FileProgressResponse>(`/api/v1/files/${fileId}/progress`),
};
